import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, Search, Filter, Plus, Eye, Edit, RefreshCw, 
  CheckCircle, XCircle, Clock, AlertTriangle, Stethoscope,
  Video, Phone, MapPin, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import AppointmentManagementDialog from './AppointmentManagementDialog';
import CreateAppointmentDialog from './CreateAppointmentDialog';

const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending', icon: Clock },
  scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled', icon: Calendar },
  confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed', icon: CheckCircle },
  in_progress: { color: 'bg-purple-100 text-purple-800', label: 'In Progress', icon: Stethoscope },
  completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed', icon: CheckCircle },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle },
  rescheduled: { color: 'bg-orange-100 text-orange-800', label: 'Rescheduled', icon: RefreshCw },
  no_show: { color: 'bg-red-100 text-red-800', label: 'No Show', icon: AlertTriangle }
};

const MODE_ICONS = {
  in_person: MapPin,
  telemedicine: Video,
  phone: Phone,
};

const AppointmentsTab = ({ appointments = [], members = [], fetchData }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Management dialog state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showManagementDialog, setShowManagementDialog] = useState(false);
  
  // Create appointment dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Providers and services for editing
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    loadProviders();
    loadServices();
  }, []);

  const loadProviders = async () => {
    try {
      const response = await axios.get(`${API}/appointments/providers`);
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/appointments/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  // Advanced Search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm || filterStatus !== 'all' || filterType !== 'all' || dateFrom || dateTo) {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterStatus, filterType, dateFrom, dateTo]);

  const performSearch = async () => {
    setIsSearching(true);
    try {
      const params = {};
      if (searchTerm) params.q = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.appointment_type = filterType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await axios.get(`${API}/search/appointments`, { params });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      // If search endpoint fails, fallback to local filtering
      const filtered = appointments.filter(apt => {
        let matches = true;
        if (filterStatus !== 'all') matches = matches && apt.status === filterStatus;
        if (filterType !== 'all') matches = matches && apt.appointment_type === filterType;
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          matches = matches && (
            apt.member?.full_name?.toLowerCase().includes(search) ||
            apt.member?.patient_id?.toLowerCase().includes(search) ||
            apt.member?.email?.toLowerCase().includes(search)
          );
        }
        return matches;
      });
      setSearchResults(filtered);
    } finally {
      setIsSearching(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setDateFrom('');
    setDateTo('');
    setSearchResults([]);
  };

  const handleOpenManagement = (appointment) => {
    setSelectedAppointment(appointment);
    setShowManagementDialog(true);
  };

  const handleCloseManagement = () => {
    setSelectedAppointment(null);
    setShowManagementDialog(false);
  };

  const handleQuickAction = async (appointmentId, action) => {
    try {
      switch (action) {
        case 'confirm':
          await axios.post(`${API}/appointments/${appointmentId}/confirm`);
          toast.success('Appointment confirmed');
          break;
        case 'complete':
          await axios.post(`${API}/appointments/${appointmentId}/complete`);
          toast.success('Appointment completed');
          break;
        case 'cancel':
          await axios.post(`${API}/appointments/${appointmentId}/cancel`, { reason: 'Cancelled by clinic' });
          toast.success('Appointment cancelled');
          break;
        default:
          break;
      }
      fetchData();
      if (searchTerm || filterStatus !== 'all' || filterType !== 'all' || dateFrom || dateTo) {
        performSearch();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} appointment`);
    }
  };

  // Use search results if searching, otherwise use all appointments
  const displayAppointments = (searchTerm || filterStatus !== 'all' || filterType !== 'all' || dateFrom || dateTo) 
    ? searchResults 
    : appointments;

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getModeIcon = (mode) => {
    const Icon = MODE_ICONS[mode] || Calendar;
    return <Icon className="h-4 w-4 text-gray-500" />;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Stats calculation
  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed' || a.status === 'scheduled').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
  };

  return (
    <div data-testid="appointments-tab">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
          <p className="text-gray-600">Manage patient appointments and consultations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateDialog(true)} 
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="new-appointment-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-600">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-xs text-gray-600">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.confirmed}</p>
              <p className="text-xs text-gray-600">Confirmed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-xs text-gray-600">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
              <p className="text-xs text-gray-600">Cancelled</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>All Appointments</CardTitle>
                <CardDescription>Search, filter and manage patient appointments</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex gap-3 items-end flex-wrap">
              <div className="flex-1 min-w-[250px]">
                <Label className="text-sm mb-2">Search Appointments</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by patient name, email, or patient ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-appointments-input"
                  />
                </div>
              </div>

              {showFilters && (
                <>
                  <div className="w-48">
                    <Label className="text-sm mb-2">Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger data-testid="filter-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="no_show">No Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-48">
                    <Label className="text-sm mb-2">Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="consultation">Consultation</SelectItem>
                        <SelectItem value="follow_up">Follow Up</SelectItem>
                        <SelectItem value="checkup">Checkup</SelectItem>
                        <SelectItem value="procedure">Procedure</SelectItem>
                        <SelectItem value="emergency">Emergency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-48">
                    <Label className="text-sm mb-2">From Date</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>

                  <div className="w-48">
                    <Label className="text-sm mb-2">To Date</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>

            {/* Search Status */}
            {isSearching && (
              <div className="text-sm text-gray-500">Searching...</div>
            )}
            {(searchTerm || filterStatus !== 'all' || filterType !== 'all' || dateFrom || dateTo) && !isSearching && (
              <div className="text-sm text-gray-600">
                Found {displayAppointments.length} appointment(s)
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Service / Type</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      {searchTerm || filterStatus !== 'all' || filterType !== 'all' || dateFrom || dateTo ? 
                        'No appointments found matching your search criteria.' : 
                        'No appointments scheduled yet.'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                displayAppointments.map((apt) => {
                  const { date, time } = formatDateTime(apt.appointment_date);
                  const canConfirm = ['pending', 'scheduled'].includes(apt.status);
                  const canComplete = ['confirmed', 'scheduled', 'in_progress'].includes(apt.status);
                  const canCancel = !['cancelled', 'completed', 'rescheduled'].includes(apt.status);
                  
                  return (
                    <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`} className="hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{apt.member?.full_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{apt.member?.patient_id || apt.member?.email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{date}</p>
                          <p className="text-xs text-gray-500">{time}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{apt.service?.name || apt.appointment_type || 'General'}</p>
                          {apt.reason && <p className="text-xs text-gray-500 truncate max-w-[150px]">{apt.reason}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {apt.provider ? (
                          <div>
                            <p className="text-sm">{apt.provider.full_name}</p>
                            <p className="text-xs text-gray-500">{apt.provider.specialty || ''}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getModeIcon(apt.appointment_mode)}
                          <span className="text-xs capitalize">{apt.appointment_mode?.replace('_', ' ') || 'In Person'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(apt.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenManagement(apt)}
                            data-testid={`manage-btn-${apt.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenManagement(apt)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedAppointment(apt);
                                  setShowManagementDialog(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {canConfirm && (
                                <DropdownMenuItem onClick={() => handleQuickAction(apt.id, 'confirm')}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                  Confirm
                                </DropdownMenuItem>
                              )}
                              {canComplete && (
                                <DropdownMenuItem onClick={() => handleQuickAction(apt.id, 'complete')}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
                                  Complete
                                </DropdownMenuItem>
                              )}
                              {canCancel && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleQuickAction(apt.id, 'cancel')}
                                    className="text-red-600"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Cancel
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Management Dialog */}
      <AppointmentManagementDialog
        appointment={selectedAppointment}
        open={showManagementDialog}
        onClose={handleCloseManagement}
        onUpdate={() => {
          fetchData();
          if (searchTerm || filterStatus !== 'all' || filterType !== 'all') {
            performSearch();
          }
        }}
        providers={providers}
        services={services}
      />
      
      {/* Create Appointment Dialog */}
      <CreateAppointmentDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => {
          fetchData();
          if (searchTerm || filterStatus !== 'all' || filterType !== 'all') {
            performSearch();
          }
        }}
      />
    </div>
  );
};

export default AppointmentsTab;
